/**
 * Затраты площадки и АУП (БМ-07): строки по видам затрат с суммой в месяц и периодом, распределение по разделам
 * бюджета и месяцам, статьи ФП. Правятся только в черновике бюджета.
 */
import {
  SITE_ALLOCATIONS,
  SITE_COST_KINDS,
  type SiteAllocation,
  type SiteCost,
  type SiteCostInput,
  type SiteCostKind,
  type SiteCostsView,
  STAFF_ROLES,
  type StaffCalc,
  type StaffRole,
  staffMonthly,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tengeShort } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

export const siteCostsQuery = (projectId: string, versionId?: string) =>
  queryOptions({
    queryKey: ['budget', 'site-costs', projectId, versionId ?? null],
    queryFn: () => api<SiteCostsView>(`/projects/${projectId}/site-costs`, { query: { versionId } }),
  });

const BTN = 'min-h-[44px] md:min-h-0';
const monthLabel = (m: string) =>
  new Intl.DateTimeFormat('ru-RU', { month: 'short', year: '2-digit' }).format(new Date(`${m}-01T00:00:00`));
const thisMonth = () => new Date().toISOString().slice(0, 7);

type Draft = SiteCostInput;
const emptyDraft = (): Draft => ({
  kind: 'itr_wages',
  name: SITE_COST_KINDS.itr_wages.title,
  monthly: 0,
  fromMonth: thisMonth(),
  toMonth: thisMonth(),
  allocation: 'by_estimate',
  shares: {},
  note: null,
  staff: null,
});
const emptyStaff = (): StaffCalc => ({ role: 'itr', headcount: 1, rate: 0, participation: 100, taxRate: 0 });

function SiteCostDialog({
  projectId,
  versionId,
  sections,
  initial,
  onClose,
}: {
  projectId: string;
  versionId: string;
  sections: SiteCostsView['bySection'];
  initial: { id: string | null; d: Draft };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(initial.d);
  const m = useMutation({
    mutationFn: () =>
      initial.id
        ? api<SiteCostsView>(`/site-costs/${initial.id}`, { method: 'PATCH', body: d })
        : api<SiteCostsView>(`/projects/${projectId}/site-costs`, { query: { versionId }, body: d }),
    onSuccess: (data) => {
      qc.setQueryData(siteCostsQuery(projectId, versionId).queryKey, data);
      qc.invalidateQueries({ queryKey: ['budget'] });
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const sharesSum = Object.values(d.shares ?? {}).reduce((s, v) => s + (v || 0), 0);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={initial.id ? 'Строка затрат площадки' : 'Новая строка затрат площадки'}
      description="Сумма в месяц за период; распределяется по разделам бюджета пропорционально смете, поровну или долями вручную (БМ-07)."
      footer={
        <>
          <Button className={BTN} variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className={BTN}
            variant="primary"
            loading={m.isPending}
            disabled={
              d.name.trim().length < 2 ||
              d.toMonth < d.fromMonth ||
              (d.allocation === 'manual' && Math.abs(sharesSum - 100) > 0.01)
            }
            onClick={() => m.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Вид затрат"
          hint={SITE_COST_KINDS[d.kind].article ?? 'статью ФП определяет финансовый менеджер'}
        >
          <Select
            value={d.kind}
            onChange={(e) => {
              const kind = e.target.value as SiteCostKind;
              setD({
                ...d,
                staff: kind === 'itr_wages' ? d.staff : null,
                kind,
                name: d.name === SITE_COST_KINDS[d.kind].title ? SITE_COST_KINDS[kind].title : d.name,
              });
            }}
          >
            {(Object.keys(SITE_COST_KINDS) as SiteCostKind[]).map((k) => (
              <option key={k} value={k}>
                {SITE_COST_KINDS[k].title}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Название">
          <Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
        </Field>
        <Field label="Сумма в месяц, ₸" hint={d.staff ? 'считается по персоналу ниже' : undefined}>
          <Input
            type="number"
            inputMode="decimal"
            readOnly={!!d.staff}
            value={(d.staff ? staffMonthly(d.staff) : d.monthly) || ''}
            onChange={(e) => setD({ ...d, monthly: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Распределение по разделам">
          <Select
            value={d.allocation ?? 'by_estimate'}
            onChange={(e) => setD({ ...d, allocation: e.target.value as SiteAllocation })}
          >
            {(Object.keys(SITE_ALLOCATIONS) as SiteAllocation[]).map((k) => (
              <option key={k} value={k}>
                {SITE_ALLOCATIONS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="С месяца">
          <Input
            type="month"
            value={d.fromMonth}
            onChange={(e) => setD({ ...d, fromMonth: e.target.value })}
          />
        </Field>
        <Field label="По месяц" error={d.toMonth < d.fromMonth ? 'раньше начала' : undefined}>
          <Input type="month" value={d.toMonth} onChange={(e) => setD({ ...d, toMonth: e.target.value })} />
        </Field>
      </div>
      {d.kind === 'itr_wages' ? (
        <fieldset className="mt-3 flex flex-col gap-3 rounded-md border border-line p-3">
          <legend className="px-1 text-[13px] font-medium">Персонал как в листе «ФОТ» (БМ-19)</legend>
          <label className="flex min-h-[44px] items-center gap-2 text-[13px] md:min-h-0">
            <input
              type="checkbox"
              checked={!!d.staff}
              onChange={(e) =>
                setD({
                  ...d,
                  staff: e.target.checked ? emptyStaff() : null,
                  monthly: !e.target.checked && d.staff ? staffMonthly(d.staff) : d.monthly,
                })
              }
            />
            Считать из численности, оклада, участия и налогов
          </label>
          {d.staff ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Кто">
                <Select
                  value={d.staff.role}
                  onChange={(e) => setD({ ...d, staff: { ...d.staff!, role: e.target.value as StaffRole } })}
                >
                  {(Object.keys(STAFF_ROLES) as StaffRole[]).map((r) => (
                    <option key={r} value={r}>
                      {STAFF_ROLES[r]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Человек">
                <Input
                  type="number"
                  inputMode="numeric"
                  value={d.staff.headcount || ''}
                  onChange={(e) =>
                    setD({ ...d, staff: { ...d.staff!, headcount: Math.floor(Number(e.target.value)) || 1 } })
                  }
                />
              </Field>
              <Field label="Оклад на человека в месяц, ₸">
                <Input
                  type="number"
                  inputMode="decimal"
                  value={d.staff.rate || ''}
                  onChange={(e) => setD({ ...d, staff: { ...d.staff!, rate: Number(e.target.value) || 0 } })}
                />
              </Field>
              <Field label="Трудовое участие, %" hint="доля времени на проекте; 100 — полностью">
                <Input
                  type="number"
                  inputMode="decimal"
                  value={d.staff.participation}
                  onChange={(e) =>
                    setD({ ...d, staff: { ...d.staff!, participation: Number(e.target.value) || 0 } })
                  }
                />
              </Field>
              <Field label="Налоги и отчисления с ФОТ, %" hint="ставку задаёт компания">
                <Input
                  type="number"
                  inputMode="decimal"
                  value={d.staff.taxRate}
                  onChange={(e) =>
                    setD({ ...d, staff: { ...d.staff!, taxRate: Number(e.target.value) || 0 } })
                  }
                />
              </Field>
            </div>
          ) : null}
        </fieldset>
      ) : null}
      {d.allocation === 'manual' ? (
        <fieldset className="mt-3 flex flex-col gap-2 rounded-md border border-line p-3">
          <legend className="px-1 text-[13px] font-medium">
            Доли разделов, % (сумма {Math.round(sharesSum * 100) / 100})
          </legend>
          {sections.map((s) => (
            <div key={s.sectionLineId} className="flex items-center gap-3 text-[13px]">
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{s.name}</span>
              <Input
                type="number"
                inputMode="decimal"
                aria-label={`Доля раздела ${s.name}, %`}
                className="w-24"
                value={d.shares?.[s.sectionLineId] ?? ''}
                onChange={(e) =>
                  setD({
                    ...d,
                    shares: { ...(d.shares ?? {}), [s.sectionLineId]: Number(e.target.value) || 0 },
                  })
                }
              />
            </div>
          ))}
        </fieldset>
      ) : null}
      <Field label="Примечание" className="mt-3">
        <Textarea
          rows={2}
          value={d.note ?? ''}
          onChange={(e) => setD({ ...d, note: e.target.value || null })}
        />
      </Field>
    </Dialog>
  );
}

export function SiteCostsPanel({ projectId, versionId }: { projectId: string; versionId: string }) {
  const qc = useQueryClient();
  const q = useQuery(siteCostsQuery(projectId, versionId));
  const [editing, setEditing] = useState<{ id: string | null; d: Draft } | null>(null);
  const [showMonths, setShowMonths] = useState(false);
  const del = useMutation({
    mutationFn: (id: string) => api<SiteCostsView>(`/site-costs/${id}`, { method: 'DELETE' }),
    onSuccess: (data) => {
      qc.setQueryData(siteCostsQuery(projectId, versionId).queryKey, data);
      qc.invalidateQueries({ queryKey: ['budget'] });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  const toDraft = (c: SiteCost): Draft => ({
    kind: c.kind,
    name: c.name,
    monthly: c.monthly ?? 0,
    fromMonth: c.fromMonth,
    toMonth: c.toMonth,
    allocation: c.allocation,
    shares: c.shares,
    note: c.note,
    staff: c.staff,
  });
  return (
    <Panel
      title="Затраты площадки и АУП"
      meta={`БМ-07: ${tengeShort(v.total)} за ${v.months.length} мес., входят в плановую себестоимость`}
      bodyClassName="p-0"
      actions={
        <>
          <button
            type="button"
            className={clsx(BTN, 'text-[13px] text-accent-text underline-offset-2 hover:underline')}
            onClick={() => setShowMonths((x) => !x)}
          >
            {showMonths ? 'скрыть по месяцам' : 'по разделам и месяцам'}
          </button>
          {v.canEdit ? (
            <Button
              className={BTN}
              variant="primary"
              icon={<Plus className="size-4" />}
              onClick={() => setEditing({ id: null, d: emptyDraft() })}
            >
              Строка затрат
            </Button>
          ) : null}
        </>
      }
    >
      {v.items.length ? (
        <ul className="divide-y divide-line">
          {v.items.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-1 px-5 py-2.5 text-[13px] md:flex-row md:items-center md:gap-4"
            >
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                <span className="font-medium">{c.name}</span> <Badge>{c.title}</Badge>
                <span className="block text-[12px] text-ink-2">
                  {monthLabel(c.fromMonth)} — {monthLabel(c.toMonth)} · {c.months} мес. ·{' '}
                  {SITE_ALLOCATIONS[c.allocation]}
                  {c.staff
                    ? ` · ${STAFF_ROLES[c.staff.role]}: ${c.staff.headcount} чел. × ${tengeShort(c.staff.rate)} × ${c.staff.participation}% + налоги ${c.staff.taxRate}%`
                    : ''}
                  {c.article ? ` · ${c.article}` : ' · статья ФП не задана'}
                </span>
              </span>
              <span className="num text-ink-2">{tengeShort(c.monthly)} / мес.</span>
              <span className="num w-[130px] font-medium md:text-right">{tengeShort(c.total)}</span>
              {v.canEdit ? (
                <span className="flex gap-2">
                  <Button
                    className={BTN}
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing({ id: c.id, d: toDraft(c) })}
                  >
                    Изменить
                  </Button>
                  <Button
                    className={BTN}
                    size="sm"
                    variant="ghost"
                    loading={del.isPending}
                    onClick={() => {
                      if (window.confirm(`Удалить «${c.name}»?`)) del.mutate(c.id);
                    }}
                  >
                    Удалить
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 py-3 text-[13px] text-ink-2">
          Строк пока нет: ФОТ ИТР, охрана, клининг, вода, ГСМ, ТО техники, питание, лизинг, аренда — суммой в
          месяц за период.
        </p>
      )}
      {showMonths && v.bySection.length ? (
        <div className="overflow-auto border-t border-line">
          <table className="text-[12px]">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="py-2 pr-2 pl-5 text-left font-medium">Раздел</th>
                <th className="px-2 py-2 text-right font-medium">Итого</th>
                {v.months.map((m) => (
                  <th key={m} className="px-2 py-2 text-right font-medium whitespace-nowrap">
                    {monthLabel(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {v.bySection.map((s) => (
                <tr key={s.sectionLineId} className="border-t border-line">
                  <td className="py-1.5 pr-2 pl-5 [overflow-wrap:anywhere]">{s.name}</td>
                  <td className="num px-2 py-1.5 text-right font-medium">{tengeShort(s.total)}</td>
                  {v.months.map((m) => (
                    <td key={m} className="num px-2 py-1.5 text-right whitespace-nowrap">
                      {s.byMonth[m] ? tengeShort(s.byMonth[m]) : '·'}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t-2 border-line-strong bg-surface-2 font-semibold">
                <td className="py-1.5 pr-2 pl-5">Итого</td>
                <td className="num px-2 py-1.5 text-right">{tengeShort(v.total)}</td>
                {v.months.map((m) => (
                  <td key={m} className="num px-2 py-1.5 text-right whitespace-nowrap">
                    {tengeShort(v.byMonth[m])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
      {editing ? (
        <SiteCostDialog
          projectId={projectId}
          versionId={versionId}
          sections={v.bySection}
          initial={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Panel>
  );
}
